import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

const STATUS_MAP = {
    0: 'Open',
    1: 'Paid',
    2: 'Invoiced',
    3: 'Partial payment',
    4: 'Invoice',
    5: 'Account',
    1002: 'Archived',
    1004: 'Lost',
    1005: 'Sub-invoice',
    1007: 'Krav sendt til forsikring',
    1008: 'Krav behandlet',
};

const PAYMENT_METHOD_MAP = {
    card: 'Card',
    cash: 'Cash',
    vipps: 'Vipps',
    invoice: 'Invoice',
    bank: 'Bank',
    account: 'Account',
    giftCard: 'Gift card',
    cardApp: 'Card (app)',
    cardExternal: 'Card (external terminal)',
    partial: 'Partial',
    insurance: 'Forsikring',
    transferred: 'Overført til egenandel',
};

const INSURANCE_STATUS_MAP = {
    0: 'Ikke aktuelt',
    1: 'Venter',
    2: 'Sendt',
    3: 'Godkjent',
    4: 'Avvist',
};

@observer
class Versions extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showRawData: false,
        };
    }

    loadVersions = async () => {
        const { type, id } = this.props;
        const { versionStore } = this.props.stores;
        if (!type || !id) {
            return;
        }
        // For sales, also include salePayment-event versions stored against
        // the sale id so payment add/delete shows up in the sale's history.
        const types = type === 'sale' ? ['sale', 'salePayment'] : [type];
        await versionStore.load({
            query: {
                type: types,
                documentId: parseInt(id, 10),
            },
            addData: ['users'],
        });
    }

    componentDidMount() {
        this.loadVersions();
    }

    toggleRawData = () => {
        this.setState({ showRawData: !this.state.showRawData });
    }

    getProductIdentifier(product) {
        return `${product.uuidv4 || product.id}-${product.createdDate || ''}`;
    }

    getProductUnitPrice(product) {
        const medicine = parseFloat(product.medicine) || 0;
        const fee = parseFloat(product.fee) || 0;
        const equipment = parseFloat(product.equipment) || 0;
        const other = parseFloat(product.other) || 0;
        const granular = medicine + fee + equipment + other;
        const base = granular > 0 ? granular : (parseFloat(product.price) || 0);
        const vat = parseFloat(product.vat) || 0;
        return base * (1 + vat / 100);
    }

    formatProduct(product) {
        const qty = parseFloat(product.qty) || 0;
        const unitPrice = this.getProductUnitPrice(product);
        const total = qty * unitPrice;
        return `${product.name || '(unnamed)'} — ${util.format(qty, 0, ',', ' ')} × ${util.format(unitPrice, 2, ',', ' ')} = ${util.format(total, 2, ',', ' ')}`;
    }

    getProductDiff(currentProducts, previousProducts) {
        const prevIds = new Set((previousProducts || []).map(p => this.getProductIdentifier(p)));
        const currIds = new Set((currentProducts || []).map(p => this.getProductIdentifier(p)));

        const added = (currentProducts || [])
            .filter(p => !prevIds.has(this.getProductIdentifier(p)))
            .map(p => this.formatProduct(p));
        const removed = (previousProducts || [])
            .filter(p => !currIds.has(this.getProductIdentifier(p)))
            .map(p => this.formatProduct(p));

        return { added, removed };
    }

    formatPaymentMethod(method) {
        return PAYMENT_METHOD_MAP[method] || method || '';
    }

    formatValue(value, key) {
        if (value === null || value === undefined) {
            return '(none)';
        }
        if (key === 'status') {
            return STATUS_MAP[value] || `${value}`;
        }
        if (key === 'insuranceStatus') {
            return INSURANCE_STATUS_MAP[value] || `${value}`;
        }
        if (key === 'paymentMethod') {
            return this.formatPaymentMethod(value) || '(none)';
        }
        if (key === 'paidDate' || key === 'saleDate' || key === 'dueDate' || key === 'createdDate' || key === 'updatedDate') {
            return value ? util.isoDate(value, false, false, true) : '(none)';
        }
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        if (typeof value === 'string' && value === '') {
            return '(empty)';
        }
        return `${value}`;
    }

    detectChanges(data, lastKnownState) {
        const changes = [];

        // Fields to skip entirely - always present, not meaningful to users
        const SKIP = new Set([
            'id', 'version', 'offline', 'user', 'customer', 'uuidv4', 'addToSet',
            'visitors', 'animals', 'journals', 'products',
            'parentSaleId', 'parentSaleNumber', 'userNumber', 'saleUser',
            'referralSaleNumber', 'invoiceNumber', 'visitorNumber', 'animalNumber',
            'sanimalisBillId', 'sanimalisBillNo', 'sanimalisRefBillId', 'animaliaId',
            'paymentUuidv4', 'calendarEvent', 'visitor', 'journal',
        ]);

        // Human-readable field names
        const FIELD_NAMES = {
            status: 'Status',
            paymentMethod: 'Payment method',
            paymentAmount: 'Payment amount',
            paidDate: 'Paid date',
            saleDate: 'Sale date',
            dueDate: 'Due date',
            saleNumber: 'Sale number',
            title: 'Title',
            description: 'Description',
            color: 'Color',
            kid: 'KID',
            insuranceStatus: 'Insurance status',
            noPackages: 'Packages',
            packedBy: 'Packed by',
            shippedBy: 'Shipped by',
            shippingMethod: 'Shipping method',
            trackingNumber: 'Tracking number',
            shippingCode: 'Shipping code',
            contactMethod: 'Contact method',
            customerDepartment: 'Department',
            deleteReason: 'Delete reason',
            saleUserName: 'Sale user',
            fikenLocation: 'Fiken location',
            isProductionAnimal: 'Production animal',
            unPaidAmount: 'Unpaid amount',
            totalPrice: 'Total price',
            vat: 'VAT',
            discount: 'Discount',
            name: 'Name',
            firstname: 'First name',
            lastname: 'Last name',
            email: 'Email',
            cellphone: 'Phone',
            address: 'Address',
            postalcode: 'Postal code',
            place: 'Place',
            readyForPickup: 'Ready for pickup',
            readyForPickupMessage: 'Ready for pickup message',
        };

        const hasLastState = Object.keys(lastKnownState).length > 0;

        Object.keys(data).forEach((key) => {
            if (SKIP.has(key)) {
                return;
            }
            const newVal = data[key];
            const oldVal = lastKnownState[key];

            // Special-case insuranceClaim — it's a nested object, but the user-facing
            // events ("krav sent", "dekning registrert") are too important to skip.
            if (key === 'insuranceClaim' && newVal && typeof newVal === 'object') {
                const oldClaim = (oldVal && typeof oldVal === 'object') ? oldVal : {};

                if (newVal.sentDate && !oldClaim.sentDate) {
                    const recipient = newVal.companyName || newVal.company || 'forsikringsselskap';
                    let line = `Krav sendt til ${recipient}`;
                    if (newVal.sentTo) line += ` (${newVal.sentTo})`;
                    if (newVal.insuranceId) line += `, forsikrings-ID ${newVal.insuranceId}`;
                    changes.push(line);
                }
                // TICKET-01734: approval with the covered amount, egenandel created at once.
                if (newVal.approvedAmount != null && oldClaim.approvedAmount == null) {
                    const recipient = newVal.companyName || newVal.company || 'forsikring';
                    changes.push(`Godkjent av ${recipient}: dekker ${util.format(newVal.approvedAmount, 2, ',', ' ')} kr`);
                    if (newVal.ownerInvoiceSale) {
                        changes.push(`Egenandel-salg opprettet: #${newVal.ownerInvoiceSale}`);
                    }
                }
                if (newVal.approvedAmount == null && oldClaim.approvedAmount != null && newVal.coveredAmount == null) {
                    changes.push('Godkjenning fra forsikring angret');
                }
                if (newVal.coveredAmount != null && oldClaim.coveredAmount == null) {
                    const recipient = newVal.companyName || newVal.company || 'forsikring';
                    changes.push(`Dekning registrert: ${util.format(newVal.coveredAmount, 2, ',', ' ')} kr fra ${recipient}`);
                    if (newVal.insurancePaidSale) {
                        changes.push(`Innbetalingssalg opprettet: #${newVal.insurancePaidSale}`);
                    }
                    if (newVal.ownerInvoiceSale && oldClaim.ownerInvoiceSale == null) {
                        changes.push(`Egenandel-faktura opprettet: #${newVal.ownerInvoiceSale}`);
                    }
                }
                return;
            }

            // Skip other objects/arrays that aren't scalar
            if (typeof newVal === 'object' && newVal !== null) {
                return;
            }

            const fieldName = FIELD_NAMES[key] || key;

            if (hasLastState && oldVal !== undefined) {
                // Compare with previous state
                if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
                    const oldFormatted = this.formatValue(oldVal, key);
                    const newFormatted = this.formatValue(newVal, key);
                    changes.push(`${fieldName}: ${oldFormatted} → ${newFormatted}`);
                }
            } else if (!hasLastState) {
                // First entry - show meaningful non-default values
                const isDefault = newVal === 0 || newVal === '' || newVal === false || newVal === null;
                if (!isDefault) {
                    changes.push(`${fieldName}: ${this.formatValue(newVal, key)}`);
                }
            }
        });

        // Also detect product array changes
        if (data.products && lastKnownState._products) {
            const { added, removed } = this.getProductDiff(data.products, lastKnownState._products);
            added.forEach(name => changes.push(`Added product: ${name}`));
            removed.forEach(name => changes.push(`Removed product: ${name}`));
        }
        // Detect visitor changes
        if (data.visitors && lastKnownState._visitors) {
            const oldNames = (lastKnownState._visitors || []).map(v => `${v.firstname || ''} ${v.lastname || ''}`.trim());
            const newNames = (data.visitors || []).map(v => `${v.firstname || ''} ${v.lastname || ''}`.trim());
            const addedVisitors = newNames.filter(n => !oldNames.includes(n));
            const removedVisitors = oldNames.filter(n => !newNames.includes(n));
            addedVisitors.forEach(name => changes.push(`Added visitor: ${name}`));
            removedVisitors.forEach(name => changes.push(`Removed visitor: ${name}`));
        }

        return changes;
    }

    getTimelineEvents() {
        const { versionStore } = this.props.stores;
        const { versions, users } = versionStore;

        // Sort oldest first for chronological timeline
        const sorted = [...versions].reverse();
        let lastProducts = [];
        let lastKnownState = {};
        const events = [];

        sorted.forEach((version) => {
            const versionUser = users.find(e => e.id === version.user) || {};
            const userName = `${versionUser.firstname || ''} ${versionUser.lastname || ''}`.trim() || 'System';
            const data = version?.data || {};
            const calledFrom = version.calledFrom || '';

            const event = {
                date: version.createdDate,
                userName,
                details: [],
                icon: 'fa-duotone fa-pen-to-square',
                iconColor: 'text-body-secondary',
                action: `Updated ${this.props.type || 'record'}`,
            };

            // salePayment-type versions (recorded by post/delete salepayment routes)
            // are stored against the parent sale's id, so they appear here when
            // loading versions for a sale.
            if (version.type === 'salePayment') {
                const method = this.formatPaymentMethod(data.paymentMethod) || data.paymentMethod || 'payment';
                const amount = data.amount != null ? util.format(data.amount, 2, ',', ' ') : '';
                const paidDate = data.paidDate ? util.isoDate(data.paidDate, false, false, true) : '';
                if (data.action === 'deleted') {
                    event.icon = 'fa-duotone fa-circle-minus';
                    event.iconColor = 'text-danger';
                    event.action = `Slettet betaling: ${method}${amount ? ` ${amount} kr` : ''}`;
                } else {
                    event.icon = 'fa-duotone fa-credit-card';
                    event.iconColor = 'text-success';
                    event.action = `Registrert betaling: ${method}${amount ? ` ${amount} kr` : ''}`;
                }
                if (paidDate) event.details.push(`Innbetalt dato: ${paidDate}`);
                if (data.description) event.details.push(data.description);
                if (data.salePaymentId) event.details.push(`Betalings-ID: #${data.salePaymentId}`);
                events.push(event);
                return; // skip the rest — no sale-state diff to detect
            }

            if (calledFrom.includes('savePayment')) {
                const method = this.formatPaymentMethod(data.paymentMethod);
                event.icon = 'fa-duotone fa-credit-card';
                event.iconColor = 'text-success';
                event.action = method ? `Registered ${method} payment` : 'Registered payment';
                if (data.saleNumber > 0) {
                    event.details.push(`Sale #${data.saleNumber}`);
                }
            } else if (calledFrom.includes('onClickSendInvoice')) {
                event.icon = 'fa-duotone fa-paper-plane';
                event.iconColor = 'text-info';
                event.action = 'Sent invoice';
            } else if (calledFrom.includes('onClickPaymentSale')) {
                event.icon = 'fa-duotone fa-credit-card';
                event.iconColor = 'text-primary';
                event.action = 'Initiated card payment';
            } else if (calledFrom.includes('onClickInvoiceSale')) {
                event.icon = 'fa-duotone fa-file-invoice';
                event.iconColor = 'text-primary';
                event.action = 'Registered invoice';
            } else if (calledFrom.includes('onClickOtherSale')) {
                event.icon = 'fa-duotone fa-money-bill';
                event.iconColor = 'text-primary';
                event.action = 'Registered other payment';
            } else if (calledFrom.includes('onClickRemoveProduct')) {
                event.icon = 'fa-duotone fa-circle-minus';
                event.iconColor = 'text-danger';
                if (data.products) {
                    const { removed } = this.getProductDiff(data.products, lastProducts);
                    event.action = 'Removed product';
                    if (removed.length > 0) {
                        event.details = removed;
                    }
                } else {
                    event.action = 'Removed product';
                }
            } else if (calledFrom.includes('searchProductCallback') || calledFrom.includes('onClickNewProduct')) {
                event.icon = 'fa-duotone fa-circle-plus';
                event.iconColor = 'text-success';
                if (data.products) {
                    const { added } = this.getProductDiff(data.products, lastProducts);
                    event.action = 'Added product';
                    if (added.length > 0) {
                        event.details = added;
                    }
                } else {
                    event.action = 'Added product';
                }
            } else if (calledFrom.includes('onChangeProductLine') || calledFrom.includes('onEditProductLine')) {
                event.icon = 'fa-duotone fa-pen';
                event.iconColor = 'text-warning';
                event.action = 'Edited product details';
            } else if (calledFrom.includes('onClickSaveSale') || calledFrom === 'salePayment.saveSale') {
                event.icon = 'fa-duotone fa-floppy-disk';
                event.iconColor = 'text-body-secondary';
                event.action = 'Saved sale';
                // Show what was saved
                const changes = this.detectChanges(data, lastKnownState);
                if (changes.length > 0) {
                    event.details = changes;
                }
            } else if (calledFrom.includes('onClickParkSale')) {
                event.icon = 'fa-duotone fa-circle-pause';
                event.iconColor = 'text-warning';
                event.action = 'Parked sale';
            } else if (calledFrom.includes('onClickMarkAsPaid')) {
                event.icon = 'fa-duotone fa-circle-check';
                event.iconColor = 'text-success';
                event.action = 'Marked as paid';
            } else if (calledFrom.includes('onClickMarkAsDone')) {
                event.icon = 'fa-duotone fa-circle-check';
                event.iconColor = 'text-success';
                event.action = 'Marked as done';
            } else if (calledFrom.includes('onClickMarkAsReadyForPickup')) {
                event.icon = 'fa-duotone fa-box';
                event.iconColor = 'text-info';
                event.action = 'Ready for pickup';
            } else if (calledFrom.includes('onClickUnmarkAsReadyForPickup')) {
                event.icon = 'fa-duotone fa-box-open';
                event.iconColor = 'text-warning';
                event.action = 'Unmarked ready for pickup';
            } else if (calledFrom.includes('toggleProductView')) {
                event.icon = 'fa-duotone fa-toggle-on';
                event.iconColor = 'text-body-secondary';
                event.action = data.isProductionAnimal ? 'Set as production animal' : 'Set as companion animal';
            } else {
                // Generic change - compare with previous state to find actual diffs
                const changes = this.detectChanges(data, lastKnownState);
                if (changes.length > 0) {
                    event.details = changes;
                }
            }

            // Update running state for comparison
            Object.keys(data).forEach((key) => {
                const val = data[key];
                if (typeof val !== 'object' || val === null) {
                    lastKnownState[key] = val;
                } else if (key === 'insuranceClaim') {
                    // Track the insuranceClaim object so detectChanges can compare
                    // sentDate / coveredAmount across versions without re-emitting.
                    lastKnownState[key] = val;
                }
            });
            // Track arrays separately for diffing
            if (data.products) {
                lastProducts = data.products;
                lastKnownState._products = data.products;
            }
            if (data.visitors) {
                lastKnownState._visitors = data.visitors;
            }

            events.push(event);
        });

        return events;
    }

    renderTimeline(events) {
        return (
            <div>
                {events.map((event, idx) => {
                    const eventDate = util.isoDate(event.date, false, false, true);
                    const prevDate = idx > 0 ? util.isoDate(events[idx - 1].date, false, false, true) : null;
                    const showDateHeader = eventDate !== prevDate;
                    const timeStr = util.isoDate(event.date, true) || '';
                    const timePart = timeStr.split(' ')[1] ? timeStr.split(' ')[1].substring(0, 5) : '';

                    return (
                        <div key={idx}>
                            {showDateHeader && (
                                <div class='fw-bold text-body-secondary mt-3 mb-2 border-bottom pb-1'>
                                    <i class='fa-duotone fa-calendar me-1' />
                                    {eventDate}
                                </div>
                            )}
                            <div class='d-flex align-items-start mb-2 ms-2'>
                                <div class='me-2 text-body-secondary' style='min-width: 42px; font-size: 12px;'>
                                    {timePart}
                                </div>
                                <div class={`me-2 ${event.iconColor}`} style='min-width: 20px; font-size: 14px;'>
                                    <i class={event.icon} />
                                </div>
                                <div class='flex-grow-1'>
                                    <div>
                                        <strong>{event.userName}</strong>
                                        <span class='ms-1 text-body-secondary'>&mdash;</span>
                                        <span class='ms-1'>{event.action}</span>
                                    </div>
                                    {event.details.length > 0 && (
                                        <div>
                                            {event.details.map((detail, dIdx) => (
                                                <small key={dIdx} class='text-body-secondary d-block ms-0'>
                                                    <i class='fa-duotone fa-angle-right me-1' />
                                                    {detail}
                                                </small>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
                {events.length === 0 && (
                    <div class='text-body-secondary'>No version history found.</div>
                )}
            </div>
        );
    }

    renderRawTable() {
        const { versionStore } = this.props.stores;
        const { versions, users } = versionStore;

        return (
            <div class='table-responsive'>
                <table class='table table-sm table-hover table-striped table-bordered'>
                    <thead>
                        <tr>
                            <th><Text id='input.createdDate'>Created Date</Text></th>
                            <th><Text id='input.user'>User</Text></th>
                            <th><Text id='input.calledFrom'>Called From</Text></th>
                            <th><Text id='input.type'>Type</Text></th>
                            <th><Text id='input.data'>Data</Text></th>
                        </tr>
                    </thead>
                    <tbody>
                        {versions.map((version, idx) => {
                            const versionUser = users.find(e => e.id === version.user) || {};
                            const data = JSON.parse(JSON.stringify(version?.data));
                            return (
                                <tr key={idx}>
                                    <td>{util.isoDate(version.createdDate, true)}</td>
                                    <td>{versionUser.firstname} {versionUser.lastname}</td>
                                    <td>{version.calledFrom || 'n/a'}</td>
                                    <td>{version.type}</td>
                                    <td>
                                        {util.isObject(data) ? (
                                            Object.keys(data).map((key, kIdx) => (
                                                <div key={kIdx}>
                                                    <span class='badge text-bg-success me-2'>{key}</span>
                                                    {util.isObject(data[key]) ? (
                                                        <small><xmp>{JSON.stringify(data[key], null, 2)}</xmp></small>
                                                    ) : (
                                                        JSON.stringify(data[key])
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <span>n/a</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    render() {
        const { open = false } = this.props;
        const { showRawData } = this.state;
        const events = this.getTimelineEvents();

        return (
            <details open={open}>
                <summary><small class='text-body-secondary'>Show version history</small></summary>
                <div class='px-3 py-2 mb-3' style='font-size: 13px;'>
                    <div class='d-flex justify-content-between align-items-center mb-2'>
                        <h6 class='mb-0'>
                            <i class='fa-duotone fa-clock-rotate-left me-1' />
                            <Text id='input.versions'>Version History</Text>
                        </h6>
                        <button
                            type='button'
                            class='btn btn-sm btn-outline-secondary'
                            onClick={this.toggleRawData}
                        >
                            <i class={`fa-duotone ${showRawData ? 'fa-timeline' : 'fa-code'} me-1`} />
                            {showRawData ? 'Timeline' : 'Raw data'}
                        </button>
                    </div>

                    {showRawData ? this.renderRawTable() : this.renderTimeline(events)}
                </div>
            </details>
        );
    }
}

export default Versions;
